import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { environment } from '../../../environments/environment';
import { WINDOW } from '../tokens/window';

/**
 * Boots analytics once:
 * - If `gtmContainerId` is set → load GTM (dataLayer-first; GA4 tags live in GTM).
 * - Else if `gaMeasurementId` is set → load gtag.js directly.
 * Never load both, or the same hits will double-count.
 */
@Injectable({
  providedIn: 'root',
})
export class GaLoaderService {
  readonly #document = inject(DOCUMENT);
  readonly #window = inject(WINDOW);
  readonly #platformId = inject(PLATFORM_ID);
  #loaded = false;

  get isLoaded(): boolean {
    return this.#loaded;
  }

  /** Prefer calling this; routes to GTM or GA4 based on environment. */
  load(): void {
    if (!isPlatformBrowser(this.#platformId) || this.#loaded) {
      return;
    }

    const gtmId = environment.gtmContainerId?.trim();
    const gaId = environment.gaMeasurementId?.trim();

    if (gtmId) {
      this.#loadGtm(gtmId);
      this.#loaded = true;
      return;
    }

    if (gaId) {
      this.#loadGa4(gaId);
      this.#loaded = true;
      return;
    }

    console.warn('[Analytics] No gtmContainerId or gaMeasurementId — disabled.');
  }

  /** @deprecated Use {@link load} */
  loadGa4(): void {
    this.load();
  }

  #loadGtm(containerId: string): void {
    const win = this.#window;
    win.dataLayer = win.dataLayer ?? [];
    win.dataLayer.push({
      'gtm.start': Date.now(),
      event: 'gtm.js',
    });

    const script = this.#document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
    this.#document.head.appendChild(script);

    // noscript iframe fallback for non-JS (optional but recommended by Google)
    const noscript = this.#document.createElement('noscript');
    const iframe = this.#document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    this.#document.body.insertBefore(noscript, this.#document.body.firstChild);
  }

  #loadGa4(gaId: string): void {
    const win = this.#window;
    win.dataLayer = win.dataLayer ?? [];

    if (typeof win.gtag !== 'function') {
      win.gtag = function gtag(..._args: unknown[]) {
        // eslint-disable-next-line prefer-rest-params
        win.dataLayer.push(arguments as unknown as (typeof win.dataLayer)[number]);
      };
    }

    const script = this.#document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`;
    this.#document.head.appendChild(script);

    win.gtag('js', new Date());
    win.gtag('config', gaId, {
      send_page_view: false,
      anonymize_ip: true,
    });
  }
}
